// Cost per shipped artifact detail (headline metric #2). A top-level
// Ticket/Feature/PR toggle scopes the section, with a Complexity filter beside it
// (Feature/PR only — see renderCaControls); below them, four stacked panels:
//   1. Cost breakdown treemap — ticket or feature (hierarchical) or PR (flat).
//   2. AI spend converted vs unconverted — burn for the toggled kind, with a
//      Break-down-by split: none | team (admin-only grouped bars) | epic
//      (ticket-only categorical breakdown with drill-in).
//   3. Cost per shipped unit BY TEAM (admin-only) — whole-window measure rows.
//   4. Throughput — "Jira items closed" / "Features shipped" / "PRs merged". For PRs
//      it counts every merged PR you contributed to (authored OR reviewed), matching
//      the KPI. On tickets it carries its own Type filter (issuetypes discovered from
//      the window), which narrows THIS PANEL ONLY — see renderItypeCtl. Its own
//      Break-down-by (none | team) swaps the line for one line per team.
// Every panel and the headline tile read the SAME population for the toggled kind
// (Store.shippedPopulation) — for tickets that is the whole Jira tree, epics
// included, so panel 3 counts an epic on top of the stories that did its work. The
// nouns below say "closed Jira items", not "delivered", for that reason (AL-146).
// The window comes from the top-level selector (state.days); the curve bucket is
// derived from it (short windows get fine buckets) unless the user picks one.
import { state, $, esc, get, usd, dayOf, autoBucket, scopeQs, canCompareTeams, kpiDelta, CX_LABELS, cxLabelList } from '../core'
import { msHtml, msWire } from '../multiselect'
import { loadKpis, paintKpis, setCaKpiOverride, openMetric } from '../kpis'
import { stackChart, breakdownBars, valueLineChart, groupedStackChart, measureBars, TEAM_COLORS } from '../charts'
import { renderFeatTreemap } from '../featTreemap'
import { filterByArtifactId, setView } from '../sessions'
import { applySkillParams } from '../skills'
import { syncHash } from '../router'
import { renderAskBanner } from '../askbanner'
import { bdRow } from './breakdownRow'
import { itypeChecked, itypeToggle, itypeReconcile, itypeSubLabel, type ItypeOption } from '../itypeFilter'

var caReqId = 0;

// Per-kind wording, in one table so the three panels, the legend, the empty states
// and the notes can't disagree about what the toggled kind is called. `unit`/`units`
// name one artifact; `verb` is what happened to it; `flow` titles panel 3; `root` is
// the treemap's synthetic root tile; `hint` completes an empty state ("… — <hint>").
var CA_KINDS = {
  ticket: {
    unit: 'Jira item', units: 'Jira items', verb: 'closed',
    tmTitle: 'Ticket cost breakdown', tmSub: 'total spend per epic, story &amp; subtask',
    flow: 'Jira items closed', root: 'All tickets',
    hint: 'close a ticket with linked sessions'
  },
  feature: {
    unit: 'feature', units: 'features', verb: 'shipped',
    tmTitle: 'Feature cost breakdown', tmSub: 'total spend per feature &amp; sub-features',
    flow: 'Features shipped', root: 'All features',
    hint: 'mark a feature shipped in the Features tab'
  },
  pr: {
    unit: 'PR', units: 'PRs', verb: 'merged',
    tmTitle: 'PR cost breakdown', tmSub: 'total spend per merged PR',
    flow: 'PRs merged', root: 'All PRs',
    hint: 'author or review a PR that merges'
  }
};
function caK() { return CA_KINDS[state.ca.kind] || CA_KINDS.feature; }
function caNoun() { return caK().units; }
function caWinLabel() { return state.days === 'all' ? 'all time' : 'last ' + state.days + ' days'; }
function caBucket() { return autoBucket(state.ca.bucket); }

export function renderCostArtifact() {
  // The epic axis only exists on the ticket tree — a stale drill-in against
  // another kind would request a breakdown of a tree that isn't there. Team
  // splits are NOT normalized away when hidden (a user/team filter, a role
  // change): caSplit()/caFlowSplit() below treat them as 'none' while hidden,
  // so clearing the filter restores the admin's explicit choice — hide, don't
  // forget (the same rule the kind toggle already follows).
  if (state.ca.by === 'epic' && state.ca.kind !== 'ticket') { state.ca.by = ''; state.ca.within = ''; state.ca.crumb = []; }
  // A clicked team row takes over the whole section as a detail "page" (Back
  // returns here). Guarded by canCompare() so a filter/role change that hides
  // the team UI also drops the drill-in rather than stranding the user on a page
  // they can no longer reach the entry point for.
  if (state.ca.teamDetail && canCompare()) { renderCaTeamDetail(); return; }
  if (state.ca.teamDetail) state.ca.teamDetail = null;
  var pr = state.ca.kind === 'pr';
  var K = caK();
  var shipNoun = K.unit;
  var shipVerb = K.verb;
  var win = esc(caWinLabel());
  var tmTitle = K.tmTitle;
  var tmSub = esc(caWinLabel()) + ' &middot; ' + K.tmSub;
  $('#metric-detail').innerHTML =
    '<div class="ca-controls" id="ca-controls"></div>' +
    // 1. Cost breakdown treemap (feature or PR, per the top-level toggle).
    '<div class="panel">' +
      '<div class="panel-head"><h2>' + tmTitle + ' <span class="metric-sub">' + tmSub + '</span></h2></div>' +
      '<div id="ca-feat"></div>' +
      '<div class="sr-legend" id="ca-feat-legend"></div>' +
    '</div>' +
    // 2. AI spend converted/unconverted (burn for the toggled kind). Two controls,
    //    matching the total-spend page's pair: "Bucket" picks the time granularity;
    //    "Break down by" picks the SPLIT — none (one combined bar per bucket), team
    //    (admin-only: grouped per-team bars, same reading), or epic (ticket-only: the
    //    categorical breakdown, which replaces the time axis, so the Bucket seg hides
    //    with it). No separate type filter, because a filter over a hierarchy
    //    double-counts (see Store.costBreakdown). On the epic axis, drill-in is the
    //    filter: click a bar to descend, the breadcrumb walks back up, and a terminal
    //    bar offers "Sessions →" instead.
    '<div class="panel">' +
      '<div class="panel-head">' +
        '<h2>AI spend: converted vs unconverted <span class="metric-sub" id="ca-burn-sub">' + win + ' &middot; dated at session start time</span></h2>' +
        '<span style="display:inline-flex;align-items:center;gap:8px">' +
          '<span id="ca-bucket-wrap" style="display:inline-flex;align-items:center;gap:8px"><span class="sr-lbl">Bucket</span><span class="seg" id="ca-bucket"></span></span>' +
          '<span class="sr-lbl">Break down by</span><select class="sr-by" id="ca-by"></select>' +
        '</span>' +
      '</div>' +
      '<div id="ca-burn-head"></div>' +
      '<div id="ca-burn"></div>' +
      '<div class="sr-legend" id="ca-burn-legend">' +
        '<span class="leg"><span class="swatch" style="background:#0f7a55"></span>converted (linked to a ' + esc(shipVerb) + ' ' + esc(shipNoun) + ')</span>' +
        '<span class="leg"><span class="swatch" style="background:#ece7dc"></span>unconverted &mdash; in-flight or never ' + esc(shipVerb) + '</span>' +
      '</div>' +
      '<div class="card-note" id="ca-burn-note"></div>' +
    '</div>' +
    // 3. Cost per shipped unit by team (admin-only, whole-window). Deliberately NOT
    //    time-bucketed: a per-bucket spend÷shipped ratio is undefined in any bucket a
    //    team ships nothing in — the time dimension lives in panels 2 and 4 instead.
    (canCompare()
      ? '<div class="panel">' +
          '<div class="panel-head"><h2>Cost per ' + shipVerb + ' ' + shipNoun + ' by team <span class="metric-sub">' + win + ' &middot; each row is what selecting that team shows</span></h2></div>' +
          '<div class="bars" id="cat-kpi"><div class="empty">Loading…</div></div>' +
        '</div>'
      : '') +
    // 4. Throughput — one graph per kind (see CA_KINDS.flow). The note is per-kind and
    //    load-bearing for tickets: the count spans every level of the tree, so a closing
    //    epic adds a count beside the stories that did its work. Without that said, a lumpy
    //    line reads as a delivery spike — and the Type filter beside the title is the way
    //    OUT of that lumpiness, since deselecting Subtask leaves the increments that a
    //    reader would call delivered. The filter belongs to this panel alone: a count of
    //    artifacts has one type each and is safe to filter, whereas the money above is
    //    shared between an epic and its subtask, so filtering it by type double-counts.
    //    Populated from the response (the options are discovered per window), hence a
    //    placeholder here rather than markup.
    '<div class="panel">' +
      '<div class="panel-head"><h2 id="ca-flow-title"></h2>' +
        '<span style="display:inline-flex;align-items:center;gap:16px"><span id="ca-itype-ctl"></span>' +
        (canCompare()
          ? '<span style="display:inline-flex;align-items:center;gap:8px"><span class="sr-lbl">Break down by</span><select class="sr-by" id="ca-flow-by">' +
              '<option value="">none</option>' +
              '<option value="team"' + (state.ca.flowBy === 'team' ? ' selected' : '') + '>Team</option>' +
            '</select></span>'
          : '') +
        '</span>' +
      '</div>' +
      '<div id="ca-flow-chart"></div>' +
      '<div class="sr-legend" id="ca-flow-legend"></div>' +
      (pr ? '<div class="card-note">Includes every merged PR you contributed to &mdash; authored or reviewed.</div>' : '') +
      (state.ca.kind === 'ticket'
        ? '<div class="card-note">Counts every level of the Jira tree &mdash; an epic closing counts alongside the ' +
          'stories and subtasks that did its work, so read this as items closed, not increments delivered.</div>'
        : '') +
    '</div>' +
    // 5. Unconverted spend detail — the pale band as a per-artifact table, always
    //    on. The "$X unconverted →" link in the burn header just scrolls here.
    '<div class="panel" id="ca-unconv-panel">' +
      '<div class="panel-head">' +
        '<h2>Unconverted spend <span class="metric-sub" id="ca-unconv-sub"></span></h2>' +
      '</div>' +
      '<div id="ca-unconv"></div>' +
      '<div class="card-note" id="ca-unconv-note"></div>' +
    '</div>' +
    '<div class="card-note" id="ca-note"></div>' +
    (canCompare()
      ? '<div class="card-note">Team splits use the same team scoping the team filter uses, so a team\'s bars, rows and lines here always match that team\'s own view. Teamless users\' work appears under no team.</div>'
      : '');
  renderCaControls();
  renderBurnCtl();
  var fb = $('#ca-flow-by');
  if (fb) fb.onchange = function () {
    state.ca.flowBy = fb.value;
    renderFlow(); // repaints from whichever cache the mode reads
    if (state.ca.flowBy === 'team' && (!caTeams || caTeams.error)) loadCaTeams();
  };
  loadTreemap();
  loadCostArtifact(); // also refreshes the team data + epic breakdown, per mode
}

export function renderCaControls() {
  // Order is presentation only — which button starts `on` is state.ca.kind (the
  // smart default in kpis.ts), never this array's first entry. Feature, PR, then
  // Ticket: narrowest unit of delivered work outward to the whole Jira tree.
  var type = [['feature', 'Feature'], ['pr', 'PR'], ['ticket', 'Ticket']].map(function (o) {
    return '<button class="' + (o[0] === state.ca.kind ? 'on' : '') + '" data-k="' + o[0] + '">' + o[1] + '</button>';
  }).join('');
  var el = $('#ca-controls');
  if (!el) return;
  var L = CX_LABELS;
  // Complexity is a FEATURE/PR control only. A ticket's complexity IS its story points
  // (83 of 108 ticket rows now carry one, since the gateway started asking Jira for
  // customfield_10016), but the store's complexityWhere returns '' for any kind other
  // than feature/pr — so on the ticket view every box would filter nothing while
  // looking like it filtered something, which is worse than not offering it.
  //
  // Un-gating this needs a POINTS scale, not this one: both existing scales are wrong
  // for an estimate. The PR buckets are diff line counts (trivial = 0–10 lines, xl =
  // 1500+), so every Fibonacci estimate collapses into `trivial`; the feature buckets
  // are a 1–5 ordinal, which silently reads a 5-point story as "xl" and has no bucket
  // at all for 8, 13, 21 or 34 — the values a third of these rows actually carry.
  var cxKind = state.ca.kind === 'pr' || state.ca.kind === 'feature';
  // No window subtitle for features (the ordinal buckets have no line range).
  // Default is every bucket checked (empty state.ca.complexity ⇒ no filter ⇒ all
  // boxes on); a partial selection stores just the checked keys.
  var cxOpts = state.ca.kind === 'pr'
    ? [['trivial', L.trivial, '1–10 lines'], ['small', L.small, '11–100'], ['medium', L.medium, '101–500'], ['large', L.large, '501–1.5k'], ['xl', L.xl, '1.5k+']]
    : [['trivial', L.trivial, ''], ['small', L.small, ''], ['medium', L.medium, ''], ['large', L.large, ''], ['xl', L.xl, ''], ['none', L.none, '']];
  var cxSel = state.ca.complexity ? state.ca.complexity.split(',') : null; // null ⇒ all selected
  // Artifact (the section-scoping toggle, prominent seg-primary) + Complexity both
  // scope the KPI tile and every chart below. The Bucket seg and the Break-down-by
  // select render into the AI-spend panel header (renderBurnCtl) since they only
  // affect the time-series charts, not the treemap.
  el.innerHTML =
    '<div class="sr-ctrl-row"><span class="sr-lbl">Artifact</span><span class="seg seg-primary" id="ca-type">' + type + '</span>' +
      (cxKind
        ? '<span style="margin-left:18px">' + msHtml('ca-cx', 'Complexity', cxSel ? cxSel.length : 0) + '</span>'
        : '') + '</div>';
  Array.prototype.forEach.call($('#ca-type').children, function (btn) {
    btn.onclick = function () {
      state.ca.kind = btn.getAttribute('data-k');
      state.ca.userPicked = true; // stick to this choice; the headline tile mirrors it
      state.ca.complexity = '';
      // Issuetypes are ticket-only, and they'd survive a round trip through Feature/PR
      // otherwise — a filter reapplied on return that the user set a kind ago.
      state.ca.itype = '';
      // The epic axis and a drill-in are ticket-only — meaningless under Feature/PR,
      // so leaving them set would request a breakdown of a tree the new kind doesn't
      // have. The TEAM split survives, though: it is kind-independent, and undoing an
      // explicit choice on an unrelated toggle would read as the control forgetting.
      if (state.ca.by !== 'team') state.ca.by = '';
      state.ca.within = ''; state.ca.crumb = [];
      renderCostArtifact();
      loadKpis();
    };
  });
  // Complexity as the shared multiselect. Same semantics as the old checkbox
  // row: '' ⇒ no filter (every box checked, badge dark), every box checked
  // collapses back to '', and the last box can't be unchecked (minOne).
  if (cxKind) msWire({
    id: 'ca-cx',
    options: cxOpts.map(function (o) { return { value: o[0], label: o[1] + (o[2] ? ' (' + o[2] + ')' : '') }; }),
    selected: cxSel || cxOpts.map(function (o) { return o[0]; }),
    minOne: true,
    badge: function (vals) { return vals.length === cxOpts.length ? 0 : vals.length; },
    onChange: function (vals) {
      state.ca.complexity = vals.length === cxOpts.length ? '' : vals.join(',');
      loadTreemap(); // treemap is complexity-scoped too (cache keyed by the filter)
      loadCostArtifact();
    },
  });
}

// ── Team splits (admin only) ─────────────────────────────────────────────────
// The burn panel's "team" split, the per-team unit-cost rows and the throughput
// panel's "team" split all read ONE response from /api/cost-artifact-teams,
// which runs the identical scoped queries the team dropdown drives — a team's
// bars and lines here are exactly what selecting that team shows. The server
// 403s non-admins, so the same predicate gates every scrap of team UI.

// One predicate with totalSpend's team breakdown — see core.canCompareTeams.
// The server independently honors scope_user/scope_team and 403s non-admins,
// so hiding here is the UX half, never the guard.
function canCompare() { return canCompareTeams(); }

// The EFFECTIVE splits: state.ca.by/flowBy keep the admin's choice even while
// a user/team filter hides the team UI; consumers read these, never the raw
// state, so a hidden 'team' renders as 'none' and returns when the filter
// clears.
function caSplit() { return state.ca.by === 'team' && !canCompare() ? '' : state.ca.by; }
function caFlowSplit() { return state.ca.flowBy === 'team' && !canCompare() ? '' : state.ca.flowBy; }

function teamLegend(teams) {
  return teams.map(function (t, i) {
    return '<span class="leg"><span class="swatch" style="background:' + TEAM_COLORS[i % TEAM_COLORS.length] + '"></span>' + esc(t.name) + '</span>';
  }).join('');
}

// The burn panel's two controls (rendered into its header): the Bucket seg —
// hidden on the epic axis, which replaced the time axis — and the Break-down-by
// select (none | team | epic), the same control pair the total-spend page has.
function renderBurnCtl() {
  var wrap = $('#ca-bucket-wrap');
  if (wrap) wrap.style.display = caSplit() === 'epic' ? 'none' : '';
  var bktEl = $('#ca-bucket');
  if (bktEl) {
    bktEl.innerHTML = [['day', 'day'], ['week', 'week'], ['month', 'month']].map(function (o) {
      return '<button class="' + (o[0] === caBucket() ? 'on' : '') + '" data-b="' + o[0] + '">' + o[1] + '</button>';
    }).join('');
    Array.prototype.forEach.call(bktEl.children, function (btn) {
      btn.onclick = function () {
        state.ca.bucket = btn.getAttribute('data-b');
        renderBurnCtl();
        loadCostArtifact(); // refetches the team curves too — both series re-bucket
      };
    });
  }
  var sel = $('#ca-by');
  if (sel) {
    sel.innerHTML = '<option value="">none</option>' +
      (canCompare() ? '<option value="team">Team</option>' : '') +
      (state.ca.kind === 'ticket' ? '<option value="epic">epic</option>' : '');
    sel.value = caSplit();
    sel.onchange = function () {
      // Any split change returns to the TOP level: a drill-in belongs to the
      // axis you leave, and silently keeping it would show a narrowed chart
      // under a control that says nothing about the narrowing.
      state.ca.by = sel.value; state.ca.within = ''; state.ca.crumb = [];
      renderBurnCtl();
      if (state.ca.by === 'epic') loadBreakdown();
      else if (state.ca.by === 'team') { renderTeamBurn(); if (!caTeams || caTeams.error) loadCaTeams(); }
      // Back to the combined time axis: repaint from cache when we have it.
      // An ERROR sentinel is not a cache — renderBurn would feed an undefined
      // KPI into paintKpis (TypeError) or paint an empty chart as a business
      // claim; refetch instead, which is also the retry the error deserves.
      else if (caCurves && !caCurves.error) renderBurn(caCurves);
      else loadCostArtifact();
    };
  }
}

var caTeams: any = null;
var catReqId = 0;

function loadCaTeams() {
  if (!canCompare()) return;
  var qs = ['kind=' + state.ca.kind, 'days=' + state.days, 'bucket=' + caBucket()];
  if (state.ca.complexity) qs.push('complexity=' + encodeURIComponent(state.ca.complexity));
  // Narrows the team THROUGHPUT lines only (same contract as /api/cost-artifact),
  // so the two throughput modes can't disagree about what "closed" counts.
  if (state.ca.kind === 'ticket' && state.ca.itype) qs.push('itypes=' + encodeURIComponent(state.ca.itype));
  var my = ++catReqId;
  caTeams = null;
  // Paint the loading states NOW, before the fetch: without this a kind/window
  // change left the previous team chart standing (or a blank under the wrong
  // legend) until the response landed — same discipline as loadUnconverted.
  renderCatKpi();
  if (caSplit() === 'team') renderTeamBurn();
  if (caFlowSplit() === 'team') renderFlow();
  var land = function (d) {
    if (my !== catReqId) return;
    // An error is an ERROR, never an empty dataset — an empty dataset renders
    // as a business claim ("no features shipped") the server did not make.
    caTeams = d && !d.error ? d : { error: true, buckets: [], teams: [] };
    renderCatKpi();
    if (caSplit() === 'team') renderTeamBurn();
    if (caFlowSplit() === 'team') renderFlow();
  };
  get('/api/cost-artifact-teams?' + qs.join('&') + scopeQs()).then(land, function () { land(null); });
}

// Panel 3: cost per shipped unit per team, with the volume beside it.
function renderCatKpi() {
  var el = $('#cat-kpi');
  if (!el) return;
  var K = caK();
  if (!caTeams) { el.innerHTML = '<div class="empty">Loading…</div>'; return; }
  if (caTeams.error) { el.innerHTML = '<div class="empty">No data.</div>'; return; }
  var teams = caTeams.teams || [];
  if (!teams.length) {
    el.innerHTML = '<div class="empty">No teams to compare.</div>';
    return;
  }
  // Every team gets a row, zero-shippers included — "Beta shipped nothing" is
  // information a filtered list would hide, and an ALL-zero window is that
  // same information three times over, not an empty state. Sorted
  // most-expensive-per-unit first; no-data teams sink last.
  var rows = teams.slice().sort(function (a, b) {
    return (b.kpi.current.costPerUnit || 0) - (a.kpi.current.costPerUnit || 0);
  });
  // Each row opens that team's detail page — the harness/model split of its spend
  // and the artifacts it shipped. A row is a button (role/tabindex + Enter/Space),
  // so the drill-in is keyboard-reachable, not mouse-only.
  var html = '<table><tr><th>Team</th><th class="num">' + esc(K.flow) + '</th><th class="num">Cost per ' + esc(K.verb) + ' ' + esc(K.unit) + '</th><th class="num">Total spend</th><th></th></tr>';
  rows.forEach(function (t) {
    var cur = t.kpi.current;
    // The team's whole AI spend in the window — every dollar, tracked or not, so it
    // is NOT costPerUnit × count (unit cost counts spend on shipped units only, and
    // reaches back before the window; see the panel note). It reads the same `spend`
    // the burn band's untracked total sums.
    var totalSpend = (t.burn || []).reduce(function (n, b) { return n + (b.spend || 0); }, 0);
    html += '<tr class="cat-row" role="button" tabindex="0" data-team-id="' + esc(t.id) + '" data-team-name="' + esc(t.name) + '">' +
      '<td>' + esc(t.name) + '</td>' +
      '<td class="num">' + cur.count + '</td>' +
      '<td class="num">' + (cur.count > 0 && cur.costPerUnit != null ? esc(usd(cur.costPerUnit)) : '—') + '</td>' +
      '<td class="num">' + esc(usd(totalSpend)) + '</td>' +
      '<td class="num">&rsaquo;</td>' +
    '</tr>';
  });
  el.innerHTML = html + '</table>';
  var open = function (row) {
    state.ca.teamDetail = { id: row.getAttribute('data-team-id'), name: row.getAttribute('data-team-name') };
    renderCostArtifact();
  };
  Array.prototype.forEach.call(el.querySelectorAll('.cat-row'), function (row) {
    row.onclick = function () { open(row); };
    row.onkeydown = function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(row); }
    };
  });
}

var caTeamDetail: any = null;
var caTeamDetailReqId = 0;

// The team detail "page": takes over #metric-detail with a Back control and three
// blocks — spend split by model, spend split by harness, and the artifacts this
// team shipped (names + cost). One fetch feeds all three; every number is scoped
// to the clicked team exactly as its table row is (see /api/cost-artifact-team-detail).
function renderCaTeamDetail() {
  var td = state.ca.teamDetail;
  if (!td) return;
  var K = caK();
  var win = esc(caWinLabel());
  $('#metric-detail').innerHTML =
    // Back + the Complexity filter (feature/PR only — rendered by renderCatComplexity).
    // It shares state.ca.complexity with the section, so the value the user set before
    // clicking a team carries in, and the cost-per-unit tile + shipped list obey it.
    '<div class="ca-controls cat-detail-controls">' +
      '<button class="btn" id="cat-back">&larr; Back to all teams</button>' +
      '<span id="cat-cx"></span>' +
    '</div>' +
    // Page title on the background (no card) — this is the detail's heading, not a panel.
    '<div class="metric-head" id="cat-title"><h2>' + esc(td.name) +
      ' <span class="metric-sub">' + win + ' &middot; ' + esc(K.units) + ' &middot; where this team\'s spend went and what it shipped</span></h2></div>' +
    // A — headline tiles (bare .tiles row, like the global KPI header).
    '<div class="tiles" id="cat-tiles"></div>' +
    // C — spend over time for this team, with a day/week/month bucket selector.
    '<div class="panel">' +
      '<div class="panel-head"><h2>Spend over time <span class="metric-sub" id="cat-curve-sub">' + win + '</span></h2>' +
        '<span style="display:inline-flex;align-items:center;gap:8px"><span class="sr-lbl">Bucket</span><span class="seg" id="cat-bucket"></span></span>' +
      '</div>' +
      '<div id="cat-curve"><div class="empty">Loading…</div></div>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="panel-head"><h2>Spend by model <span class="metric-sub" id="cat-model-sub"></span></h2></div>' +
      '<div class="bars" id="cat-model"><div class="empty">Loading…</div></div>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="panel-head"><h2>Spend by harness <span class="metric-sub">' + win + '</span></h2></div>' +
      '<div class="bars" id="cat-harness"><div class="empty">Loading…</div></div>' +
    '</div>' +
    // B — spend by repo.
    '<div class="panel">' +
      '<div class="panel-head"><h2>Spend by repo <span class="metric-sub">' + win + '</span></h2></div>' +
      '<div class="bars" id="cat-repo"><div class="empty">Loading…</div></div>' +
    '</div>' +
    // Skill usage — sessions per skill (reach), the count the team leans on.
    '<div class="panel">' +
      '<div class="panel-head"><h2>Skill usage <span class="metric-sub">' + win + ' &middot; sessions that used each skill</span></h2></div>' +
      '<div class="bars" id="cat-skill"><div class="empty">Loading…</div></div>' +
    '</div>' +
    '<div class="panel">' +
      '<div class="panel-head"><h2>' + esc(K.units.charAt(0).toUpperCase() + K.units.slice(1)) + ' ' + esc(K.verb) +
        ' <span class="metric-sub">' + win + ' &middot; names and build cost</span></h2></div>' +
      '<div id="cat-shipped"><div class="empty">Loading…</div></div>' +
    '</div>';
  var back = $('#cat-back');
  if (back) back.onclick = function () {
    state.ca.teamDetail = null;
    renderCostArtifact();
    loadCostArtifact();
  };
  renderCatBucketCtl();
  renderCatComplexity();
  loadCaTeamDetail(td.id);
}

// The detail page's Complexity filter — the same control renderCaControls draws for
// the section, scoped to this page. Feature/PR only (a ticket's complexity has no
// scale the store's complexityWhere accepts — see renderCaControls). Pre-seeded from
// state.ca.complexity, so it shows whatever the user set before drilling in; a change
// refetches the detail (the cost-per-unit tile + shipped list re-scope to it).
function renderCatComplexity() {
  var el = $('#cat-cx');
  if (!el) return;
  var cxKind = state.ca.kind === 'pr' || state.ca.kind === 'feature';
  if (!cxKind) { el.innerHTML = ''; return; }
  var L = CX_LABELS;
  var cxOpts = state.ca.kind === 'pr'
    ? [['trivial', L.trivial, '1–10 lines'], ['small', L.small, '11–100'], ['medium', L.medium, '101–500'], ['large', L.large, '501–1.5k'], ['xl', L.xl, '1.5k+']]
    : [['trivial', L.trivial, ''], ['small', L.small, ''], ['medium', L.medium, ''], ['large', L.large, ''], ['xl', L.xl, ''], ['none', L.none, '']];
  var cxSel = state.ca.complexity ? state.ca.complexity.split(',') : null; // null ⇒ all selected
  var cx = cxOpts.map(function (o) {
    var on = cxSel ? cxSel.indexOf(o[0]) !== -1 : true;
    return '<label class="sr-check"><input type="checkbox" class="cat-cx" value="' + o[0] + '"' + (on ? ' checked' : '') + '/> ' +
      esc(o[1]) + (o[2] ? ' <span class="sr-cnt">' + esc(o[2]) + '</span>' : '') + '</label>';
  }).join('');
  el.innerHTML = '<span class="sr-lbl">Complexity</span><span class="sr-checks">' + cx + '</span>';
  Array.prototype.forEach.call(el.querySelectorAll('.cat-cx'), function (cb) {
    cb.onchange = function () {
      var boxes = el.querySelectorAll('.cat-cx');
      var set = [];
      Array.prototype.forEach.call(boxes, function (x) { if (x.checked) set.push(x.value); });
      if (!set.length) { cb.checked = true; return; } // keep at least one bucket selected
      // Every box checked ⇒ clear the filter (also re-includes untagged artifacts).
      state.ca.complexity = set.length === boxes.length ? '' : set.join(',');
      var td = state.ca.teamDetail;
      if (td) loadCaTeamDetail(td.id);
    };
  });
}

// The Spend-over-time panel's day/week/month selector. Shares state.ca.bucket
// with the main section's burn control, so a bucket chosen here carries back —
// changing it refetches this team's curve (the granularity is server-side).
function renderCatBucketCtl() {
  var el = $('#cat-bucket');
  if (!el) return;
  el.innerHTML = [['day', 'day'], ['week', 'week'], ['month', 'month']].map(function (o) {
    return '<button class="' + (o[0] === caBucket() ? 'on' : '') + '" data-b="' + o[0] + '">' + o[1] + '</button>';
  }).join('');
  Array.prototype.forEach.call(el.children, function (btn) {
    btn.onclick = function () {
      state.ca.bucket = btn.getAttribute('data-b');
      renderCatBucketCtl();
      var td = state.ca.teamDetail;
      if (td) loadCaTeamDetail(td.id);
    };
  });
}

// Clicking a skill in the team-detail panel jumps to that skill's page on the
// Skills tab, carrying the same window AND this team, so the skill detail shows
// only this team (e.g. #/skills?skill=subtask&win=90&team=team:…). applySkillParams
// treats `team` as authoritative — it sets the shared scope, so no need to set it
// here first. Same nav functions the router drives, so the URL mirrors the state.
function gotoSkill(name) {
  if (!name) return;
  var td = state.ca.teamDetail;
  setView('skills');
  applySkillParams({ skill: name, win: state.days === 'all' ? 'all' : String(state.days), team: td ? td.id : '' });
  syncHash();
}

function loadCaTeamDetail(id) {
  var my = ++caTeamDetailReqId;
  caTeamDetail = null;
  var qs = ['team=' + encodeURIComponent(id), 'kind=' + encodeURIComponent(state.ca.kind), 'days=' + encodeURIComponent(String(state.days)), 'bucket=' + encodeURIComponent(caBucket())];
  if (state.ca.complexity) qs.push('complexity=' + encodeURIComponent(state.ca.complexity));
  var land = function (d) {
    if (my !== caTeamDetailReqId) return;
    caTeamDetail = d && !d.error ? d : { error: true, kpi: null, curve: null, byModel: { rows: [] }, byHarness: { rows: [] }, byRepo: { rows: [] }, bySkill: { rows: [] }, shipped: [] };
    renderCaTeamDetailBody();
  };
  get('/api/cost-artifact-team-detail?' + qs.join('&') + scopeQs()).then(land, function () { land(null); });
}

// A — the per-team headline tiles. Reads the /api/kpis bundle the endpoint threads
// through, so these agree with the same scope + window. Deliberately NOT a copy of
// the global header row: success rate (its "Session Outcome Rate") and tool error
// rate (dropped from that row as a plumbing measure) are omitted, and Skills used —
// which the panel below decomposes — takes their place. Each tile carries a ▲▼
// delta vs the prior window (blank on the all-time window, where previous is null).
function renderCatTiles(kpi, K) {
  if (!kpi || !kpi.current) return '';
  var cur = kpi.current, prev = kpi.previous || {};
  var tile = function (label, val, delta) {
    return '<div class="tile"><div class="label">' + esc(label) + '</div><div class="value"><span class="num">' +
      esc(val) + '</span>' + (delta || '') + '</div></div>';
  };
  var curAvg = cur.sessions > 0 ? cur.totalSpend / cur.sessions : null;
  var prevAvg = prev.sessions > 0 ? prev.totalSpend / prev.sessions : null;
  var cpu = (cur.count > 0 && cur.costPerUnit != null) ? usd(cur.costPerUnit) : '—';
  // Spend/sessions/skills have no better/worse direction (good=null → neutral accent);
  // avg $/session and cost-per-unit are cheaper-is-better (good='down').
  return tile('Total spend', usd(cur.totalSpend || 0), kpiDelta(cur.totalSpend, prev.totalSpend, 'rel', null)) +
    tile('Sessions', String(cur.sessions || 0), kpiDelta(cur.sessions, prev.sessions, 'rel', null)) +
    tile('Avg $/session', curAvg != null ? usd(curAvg) : '—', kpiDelta(curAvg, prevAvg, 'rel', 'down')) +
    tile('Cost per ' + K.verb + ' ' + K.unit, cpu, kpiDelta(cur.costPerUnit, prev.costPerUnit, 'rel', 'down')) +
    tile('Skills used', String(cur.skillsUsed || 0), kpiDelta(cur.skillsUsed, prev.skillsUsed, 'rel', null));
}

function renderCaTeamDetailBody() {
  var d = caTeamDetail;
  var K = caK();
  var mEl = $('#cat-model'), hEl = $('#cat-harness'), sEl = $('#cat-shipped');
  var rEl = $('#cat-repo'), cEl = $('#cat-curve'), tEl = $('#cat-tiles'), kEl = $('#cat-skill');
  if (!mEl || !hEl || !sEl) return; // Back was pressed before the fetch landed
  if (!d) { return; } // still loading — the placeholders stand
  if (d.error) {
    mEl.innerHTML = hEl.innerHTML = '<div class="empty">No data.</div>';
    if (rEl) rEl.innerHTML = '<div class="empty">No data.</div>';
    if (kEl) kEl.innerHTML = '<div class="empty">No data.</div>';
    if (cEl) cEl.innerHTML = '<div class="empty">No data.</div>';
    if (tEl) tEl.innerHTML = '';
    sEl.innerHTML = '<div class="empty">No data.</div>';
    return;
  }
  // A — headline tiles.
  if (tEl) tEl.innerHTML = renderCatTiles(d.kpi, K);
  // C — spend over time (one line; 0-filled buckets, so guard on a real point or
  // valueLineChart draws a flat zero line).
  if (cEl) {
    var cv = d.curve || { buckets: [], spend: [] };
    var pts = (cv.buckets || []).map(function (b, i) { return { bucket: b, y: (cv.spend || [])[i] || 0 }; });
    cEl.innerHTML = pts.some(function (p) { return p.y > 0; })
      ? valueLineChart(cv.buckets, [{ label: 'AI spend', color: '#0f7a55', points: pts }], 'usd', 'AI spend')
      : '<div class="empty">No spend in this window.</div>';
    var cSub = $('#cat-curve-sub');
    if (cSub) cSub.innerHTML = esc(caWinLabel() + ' · per ' + (cv.bucket || caBucket()));
  }
  // measureBars pairs directly with breakdown's {bucket,value} rows; a null model
  // (usage with no recorded model) renders as "—", same as elsewhere.
  var mRows = (d.byModel && d.byModel.rows) || [];
  var hRows = (d.byHarness && d.byHarness.rows) || [];
  mEl.innerHTML = mRows.length ? measureBars(mRows, 'usd') : '<div class="empty">No spend in this window.</div>';
  hEl.innerHTML = hRows.length ? measureBars(hRows, 'usd') : '<div class="empty">No spend in this window.</div>';
  var mSub = $('#cat-model-sub');
  if (mSub) mSub.innerHTML = esc(caWinLabel()) + ' &middot; total ' + esc(usd((d.byModel && d.byModel.total) || 0));
  // B — spend by repo (unresolved repo renders "—" via measureBars).
  if (rEl) {
    var rRows = (d.byRepo && d.byRepo.rows) || [];
    rEl.innerHTML = rRows.length ? measureBars(rRows, 'usd') : '<div class="empty">No spend in this window.</div>';
  }
  // Skill usage — session counts per skill ('int' format renders whole sessions).
  // Each bar is a link into that skill's page on the Skills tab (same window), so
  // the rows carry role/tabindex + click/Enter handlers, matched to kRows by index
  // (measureBars renders in the given order).
  if (kEl) {
    var kRows = (d.bySkill && d.bySkill.rows) || [];
    if (kRows.length) {
      kEl.innerHTML = measureBars(kRows, 'int');
      kEl.classList.add('cat-skill-nav');
      Array.prototype.forEach.call(kEl.querySelectorAll('.bar-row'), function (row, i) {
        var skill = kRows[i] && kRows[i].bucket;
        if (skill == null) return;
        row.setAttribute('role', 'button');
        row.setAttribute('tabindex', '0');
        row.onclick = function () { gotoSkill(skill); };
        row.onkeydown = function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); gotoSkill(skill); } };
      });
    } else {
      kEl.classList.remove('cat-skill-nav');
      kEl.innerHTML = '<div class="empty">No skills used in this window.</div>';
    }
  }
  var shipped = (d.shipped || []).slice().sort(function (a, b) { return (b.costUsd || 0) - (a.costUsd || 0); });
  if (!shipped.length) {
    sEl.innerHTML = '<div class="empty">No ' + esc(K.units) + ' ' + esc(K.verb) + ' in this window.</div>';
    return;
  }
  // Each artifact renders the way the Artifacts tab renders it — a PR by its title
  // linked to GitHub, a ticket by its key linked to Jira, a feature by its name —
  // and every row carries a "Sessions →" button, the same drill-in filterByArtifactId
  // drives from the Artifacts tab and the unconverted table. costMeasured guards the
  // $: an unmeasured artifact shows "—", never a misleading $0.
  var nameCell = function (r) {
    if (state.ca.kind === 'pr') {
      // Match the Artifacts PR table: "repo #123" as the green id link, the title
      // grey underneath. Here the title is ALSO linked to the PR (the Artifacts tab
      // leaves it plain text) — both id and title open GitHub when external_id is set.
      if (r.ident) {
        var idLabel = (r.repo ? esc(r.repo) + ' ' : '') + '#' + esc(r.ident);
        var idHtml = r.externalId
          ? '<a class="pr-link" href="' + esc(r.externalId) + '" target="_blank" rel="noopener">' + idLabel + '</a>'
          : idLabel;
        var titleHtml = r.title
          ? (r.externalId
              ? '<a class="pr-title-link" href="' + esc(r.externalId) + '" target="_blank" rel="noopener">' + esc(r.title) + '</a>'
              : '<div class="pr-title">' + esc(r.title) + '</div>')
          : '';
        return idHtml + titleHtml;
      }
      // No PR number to show — fall back to the title (or id) as the single link.
      var fb = r.title || r.id;
      return r.externalId
        ? '<a class="pr-link" href="' + esc(r.externalId) + '" target="_blank" rel="noopener">' + esc(fb) + '</a>'
        : esc(fb);
    }
    if (state.ca.kind === 'ticket') {
      var key = r.ident || r.id;
      var sub = r.title && r.title !== key ? ' <span class="sr-cnt">' + esc(r.title) + '</span>' : '';
      return (r.externalId
        ? '<a class="pr-link tkt-key" href="' + esc(r.externalId) + '" target="_blank" rel="noopener">' + esc(key) + '</a>'
        : '<span class="tkt-key">' + esc(key) + '</span>') + sub;
    }
    return esc(r.title || r.ident || r.id);
  };
  // The label shown in the Sessions filter chip — the artifact's human id, per kind.
  var artLabel = function (r) {
    if (state.ca.kind === 'pr') return r.ident ? (r.repo ? r.repo + ' ' : '') + '#' + r.ident : (r.title || r.id);
    if (state.ca.kind === 'ticket') return r.ident || r.id;
    return r.title || r.ident || r.id;
  };
  var html = '<table><tr><th>' + esc(K.unit.charAt(0).toUpperCase() + K.unit.slice(1)) + '</th><th class="num">Cost</th><th class="num">Sessions</th><th></th></tr>';
  shipped.forEach(function (r) {
    html += '<tr>' +
      '<td>' + nameCell(r) + '</td>' +
      '<td class="num">' + (r.costMeasured ? esc(usd(r.costUsd || 0)) : '—') + '</td>' +
      '<td class="num">' + (r.sessions || 0) + '</td>' +
      '<td class="num"><button class="btn" data-id="' + esc(r.id) + '" data-art="' + esc(artLabel(r)) + '">Sessions &rarr;</button></td>' +
    '</tr>';
  });
  sEl.innerHTML = html + '</table>';
  // Same destination as the Artifacts tab's "Sessions →": the Sessions list filtered
  // to this one artifact (itself, not a subtree), all time — the row's kind carries
  // the drawer's transcript focus.
  Array.prototype.forEach.call(sEl.querySelectorAll('button[data-id]'), function (btn) {
    btn.onclick = function () {
      filterByArtifactId(btn.getAttribute('data-id'), btn.getAttribute('data-art'), false, state.ca.kind);
    };
  });
}

// The "$X unconverted →" figure, from the combined response (the same
// population the detail table shows). Rendered into the unsplit header AND the
// split modes' subtitles, so no Break-down-by choice loses the number or the
// jump to the table. '' until the combined fetch lands (or on its error).
function unconvLink() {
  var d = caCurves;
  if (!d || !d.burn) return '';
  var tot = 0, conv = 0;
  d.burn.forEach(function (r) { tot += r.attributedSpend || 0; conv += r.shippedSpend || 0; });
  var pale = tot - conv;
  return pale > 0.005 ? ' &middot; <a class="show-all-link" id="ca-unconv-link">' + esc(usd(pale)) + ' unconverted &rarr;</a>' : '';
}
function wireUnconvLink() {
  var a = $('#ca-unconv-link');
  if (a) a.onclick = openUnconverted;
}

// The burn panel's team split: same reading as the combined chart, per team —
// bar = ATTRIBUTED spend (money that reached a discovered artifact of this
// kind), solid = shipped, hue = team. Untracked spend is deliberately not a
// bar — its per-team view is the total-spend page's team breakdown; here the
// story is conversion.
function renderTeamBurn() {
  if (caSplit() !== 'team') return;
  var el = $('#ca-burn');
  if (!el) return;
  var K = caK();
  var head = $('#ca-burn-head');
  if (head) head.innerHTML = ''; // per-team totals live in the bar tooltips
  var sub = $('#ca-burn-sub');
  if (sub) { sub.innerHTML = esc(caWinLabel()) + ' &middot; by team &middot; dated at session start time' + unconvLink(); wireUnconvLink(); }
  renderBurnNote();
  var d = caTeams;
  var leg = $('#ca-burn-legend');
  if (!d || d.error) {
    el.innerHTML = '<div class="empty">' + (d ? 'No data.' : 'Loading…') + '</div>';
    if (leg) leg.innerHTML = '';
    return;
  }
  var teams = d.teams || [];
  var groups = teams.map(function (t, i) {
    return {
      label: t.name,
      color: TEAM_COLORS[i % TEAM_COLORS.length],
      points: d.buckets.map(function (b, j) {
        return { bucket: b, total: t.burn[j].attributedSpend, filled: t.burn[j].shippedSpend };
      }),
    };
  });
  el.innerHTML = groupedStackChart(d.buckets, groups, 'usd', 'AI spend (USD)');
  if (leg) leg.innerHTML = teamLegend(teams) +
    '<span class="leg" style="margin-left:12px">solid = converted (linked to a ' + esc(K.verb) + ' ' + esc(K.unit) + ')</span>' +
    '<span class="leg">pale = in-flight or never ' + esc(K.verb) + '</span>';
}

// ── Unconverted spend detail ─────────────────────────────────────────────────
// The pale band as a per-artifact table: which open / won't-do items absorbed
// the window's unconverted spend. Same kind/window/complexity/scope params as
// the chart, refetched by loadCostArtifact whenever they move, so the table
// always obeys the top window selector.

var caUnconv: any = null;
var caUnconvReqId = 0;
// Rows shown before "Show all" — spend-desc, so the cap keeps the expensive
// tail-chasing rows and folds the long cheap tail behind an explicit link.
var UNCONV_TOP = 20;
var caUnconvAll = false;

function openUnconverted() {
  var p = $('#ca-unconv-panel');
  if (p) p.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function loadUnconverted(keepAll?) {
  var my = ++caUnconvReqId;
  // The Show-all expand (keepAll) fetches IN THE BACKGROUND: the table the
  // user is reading stays on screen until the full page arrives — a failed
  // expand must not eat the rows they had.
  if (!keepAll) {
    caUnconv = null;
    caUnconvAll = false; // "Show all" was a choice about THAT list, not a setting
    renderUnconverted(); // loading state
  }
  var qs = ['kind=' + encodeURIComponent(state.ca.kind), 'days=' + encodeURIComponent(String(state.days))];
  // The cap is applied SERVER-SIDE (spend-desc keeps the expensive rows): a
  // 1,000-item backlog must not ship 1,000 rows to render 20. "Show all"
  // refetches uncapped; the response's totals keep the link's label honest.
  if (!caUnconvAll) qs.push('limit=' + UNCONV_TOP);
  if (state.ca.complexity) qs.push('complexity=' + encodeURIComponent(state.ca.complexity));
  var land = function (d) {
    if (my !== caUnconvReqId) return;
    if (keepAll && (!d || d.error)) {
      // The expand failed: keep the capped page (still valid), restore the
      // Show-all link so the user can retry.
      caUnconvAll = false;
      renderUnconverted();
      return;
    }
    // An error must stay an error: {rows: []} here would render the success
    // message ("nothing unconverted — every dollar converted") off a 500.
    caUnconv = d && !d.error ? d : { error: true, rows: [] };
    renderUnconverted();
  };
  get('/api/cost-artifact-unconverted?' + qs.join('&') + scopeQs()).then(land, function () { land(null); });
}

function renderUnconverted() {
  var el = $('#ca-unconv');
  if (!el) return;
  var K = caK();
  var sub = $('#ca-unconv-sub');
  if (sub) sub.innerHTML = esc(caWinLabel()) + ' &middot; spend that reached a ' + esc(K.unit) + ' that has not ' + esc(K.verb);
  var note = $('#ca-unconv-note');
  if (!caUnconv) { el.innerHTML = '<div class="empty">Loading…</div>'; if (note) note.innerHTML = ''; return; }
  if (caUnconv.error) { el.innerHTML = '<div class="empty">No data.</div>'; if (note) note.innerHTML = ''; return; }
  var rows = caUnconv.rows || [];
  if (!rows.length) {
    el.innerHTML = '<div class="empty">Nothing unconverted in this window — every dollar that reached a ' + esc(K.unit) + ' is on one that ' + esc(K.verb) + '.</div>';
    if (note) note.innerHTML = '';
    return;
  }
  // The server already capped the page (spend-desc — the hidden tail is the
  // cheapest); the response's totals say what the cap left out, so the link is
  // an explicit disclosure, never a silent truncation.
  var shown = rows;
  var totalRows = caUnconv.totalRows != null ? caUnconv.totalRows : rows.length;
  var hidden = totalRows - shown.length;
  // The item cell renders the way the Artifacts tab renders the same artifact,
  // so one artifact reads identically wherever it appears:
  //  * PR: "repo #123", linked to GitHub via external_id.
  //  * ticket: the key (AL-126), linked to Jira when the connector filled
  //    external_id — plain text before that, same rule as the ticket table.
  //  * feature: just its NAME — the id is a machine slug nobody should read.
  var itemCell = function (r) {
    var titleSub = r.title && r.title !== r.ident ? ' <span class="sr-cnt">' + esc(r.title) + '</span>' : '';
    if (state.ca.kind === 'pr') {
      var prLabel = r.ident ? (r.repo ? r.repo + ' ' : '') + '#' + r.ident : (r.title || r.id);
      var prHtml = r.externalId
        ? '<a class="pr-link" href="' + esc(r.externalId) + '" target="_blank" rel="noopener">' + esc(prLabel) + '</a>'
        : esc(prLabel);
      return { label: prLabel, html: prHtml + (r.ident ? titleSub : '') };
    }
    if (state.ca.kind === 'feature') {
      var name = r.title || r.ident || r.id;
      return { label: name, html: esc(name) };
    }
    var key = r.ident || r.id;
    var keyHtml = r.externalId
      ? '<a class="pr-link tkt-key" href="' + esc(r.externalId) + '" target="_blank" rel="noopener">' + esc(key) + '</a>'
      : '<span class="tkt-key">' + esc(key) + '</span>';
    return { label: key, html: keyHtml + titleSub };
  };
  var html = '<table><colgroup><col /><col style="width:70px" /><col style="width:80px" /><col style="width:75px" /><col style="width:105px" /><col style="width:100px" /></colgroup>' +
    '<tr><th>' + esc(K.unit) + '</th><th>Status</th><th class="num">Spend</th><th class="num">Sessions</th><th class="num">Last activity</th><th></th></tr>';
  shown.forEach(function (r) {
    var item = itemCell(r);
    html += '<tr>' +
      '<td>' + item.html + '</td>' +
      '<td>' + esc((r.status || 'open').replace(/_/g, ' ')) + '</td>' +
      '<td class="num">' + esc(usd(r.spend || 0)) + '</td>' +
      '<td class="num">' + r.sessions + '</td>' +
      '<td class="num">' + esc(dayOf(r.lastTs)) + '</td>' +
      '<td><button class="btn" data-unconv-sess="' + esc(r.id) + '" data-art="' + esc(item.label) + '">Sessions &rarr;</button></td>' +
    '</tr>';
  });
  html += '</table>';
  if (hidden > 0) {
    var shownSum = shown.reduce(function (n, r) { return n + (r.spend || 0); }, 0);
    var hiddenSum = Math.max(0, (caUnconv.totalSpend || 0) - shownSum);
    html += '<div style="padding:8px 10px"><a class="show-all-link" id="ca-unconv-more">Show all ' + totalRows +
      ' — ' + hidden + ' more totaling ' + esc(usd(hiddenSum)) + '</a></div>';
  }
  el.innerHTML = html;
  var more = $('#ca-unconv-more');
  if (more) more.onclick = function () { caUnconvAll = true; loadUnconverted(true); };
  // Same destination as every other "Sessions →" in the section: the Sessions
  // list filtered to that one artifact (itself, not its subtree — the row's
  // money is what was charged to THIS item), all time.
  Array.prototype.forEach.call(el.querySelectorAll('[data-unconv-sess]'), function (btn) {
    btn.onclick = function () {
      filterByArtifactId(btn.getAttribute('data-unconv-sess'), btn.getAttribute('data-art'), false, state.ca.kind);
    };
  });
  Array.prototype.forEach.call(el.querySelectorAll('td:first-child'), function (td) {
    td.onclick = function (e) {
      if (e.target.tagName === 'A') return;
      td.classList.toggle('uc-expanded');
    };
  });
  if (note) note.innerHTML = esc('Same window and scope as the chart above; spend is what the ' + K.units +
    ' absorbed inside it. A block charged to two still-open items counts under both rows, so rows can sum past the band. ' +
    '“Sessions →” lists that item’s sessions across all time. Old + quiet + expensive is the row to chase.');
}

// Treemap nodes scope to the top-level window (artifacts completed in it) and the
// complexity filter — both applied server-side — while each tile stays sized by the
// artifact's all-time build cost, so the treemap decomposes the cost-per-artifact
// KPI. Cached per kind, under one (complexity, window, scope) key; a change to that
// key invalidates EVERY kind's cache (a stale entry for a kind you toggle back to
// would repaint the previous window). Tickets and features come hierarchical from
// /api/feature-costs (which kind, via ?kind=); PRs are a flat set from /api/artifacts
// (each PR a tile sized by its all-time cost).
var caNodes: Record<string, any[] | null> = { ticket: null, feature: null, pr: null };
var caTreeKey = '';
var caTreeReqId = 0;

export function loadTreemap() {
  // Cache key includes the scope: a team/repo change must invalidate the cached
  // nodes, or the treemap would repaint the previous scope's artifacts (stale +
  // cross-team). scopeQs() is '' when global, so behavior is unchanged there.
  var key = state.ca.complexity + '|' + state.days + '|' + scopeQs();
  if (caTreeKey !== key) { caNodes = { ticket: null, feature: null, pr: null }; caTreeKey = key; }
  var kind = state.ca.kind;
  var cx = state.ca.complexity ? '&complexity=' + encodeURIComponent(state.ca.complexity) : '';
  var days = '&days=' + encodeURIComponent(String(state.days));
  var myReq = ++caTreeReqId; // ignore an out-of-order response from a superseded filter/window
  if (caNodes[kind]) { renderTreemap(); return; }
  if (kind === 'pr') {
    get('/api/artifacts?kind=pr&shipped=1' + days + cx + scopeQs()).then(function (rows) {
      if (myReq !== caTreeReqId) return;
      caNodes.pr = (Array.isArray(rows) ? rows : []).map(function (r) {
        return { id: r.id, title: r.title || r.ident || r.externalId || r.id, parentId: null, ownCost: r.costUsd || 0, subtreeCost: r.costUsd || 0 };
      });
      renderTreemap();
    });
  } else {
    // `kind` must be forwarded: the endpoint defaults to the ticket tree, so without
    // it the Feature view would draw tickets under a cost-per-feature KPI.
    get('/api/feature-costs?kind=' + encodeURIComponent(kind) + days + cx + scopeQs()).then(function (d) {
      if (myReq !== caTreeReqId) return;
      caNodes[kind] = (d && d.nodes) || [];
      renderTreemap();
    });
  }
}

function renderTreemap() {
  var el = $('#ca-feat');
  if (!el) return;
  var pr = state.ca.kind === 'pr';
  var K = caK();
  var nodes = caNodes[state.ca.kind];
  var cxNote = state.ca.complexity ? ' <span class="metric-sub">complexity: ' + esc(cxLabelList(state.ca.complexity)) + '</span>' : '';
  if (!nodes) { el.innerHTML = '<div class="empty">Loading…</div>'; return; }
  if (!nodes.length) {
    var emptyMsg = state.ca.complexity
      ? 'No ' + K.units + ' match this complexity filter — widen the Complexity selection above.'
      : state.days !== 'all'
        ? 'No ' + K.units + ' ' + K.verb + ' in this window — widen the window above.'
        : 'No ' + K.verb + ' ' + K.unit + ' costs yet — ' + K.hint + ' with linked sessions.';
    el.innerHTML = '<div class="empty">' + esc(emptyMsg) + '</div>';
    var leg0 = $('#ca-feat-legend'); if (leg0) leg0.innerHTML = '';
    return;
  }
  renderFeatTreemap(el, nodes, K.root);
  var leg = $('#ca-feat-legend');
  if (leg) {
    // The second sentence for the hierarchical kinds exists because this pane and the
    // burn panel above it now show DIFFERENT totals for the same epic on purpose: only
    // spend on a closed item is drawn here, so the tile is the smaller figure. Saying it
    // where both numbers are visible is the cheap half of the AL-149 lesson — a reader
    // compares two panels on one screen whether or not they were built to be compared.
    leg.innerHTML = (pr
      ? '<span class="leg">Area &prop; cost &middot; each tile is a merged PR. Use the slider to roll up the long tail into “Other”.</span>'
      : '<span class="leg">Area &prop; subtree cost &middot; counts spend on ' + esc(K.units) + ' that ' + esc(K.verb) +
        ', so a tile can read less than the same item’s bar above. Click a tile to drill in; use the slider to roll up the long tail.</span>') + cxNote;
  }
}

// Curves for the toggled kind (one response feeds both the burn panel and the
// throughput panel: d.burn, d.throughput = "shipped", d.reviewed = PR-only).
var caCurves: any = null;

export function loadCostArtifact() {
  var qs = ['kind=' + encodeURIComponent(state.ca.kind), 'days=' + encodeURIComponent(String(state.days)), 'bucket=' + encodeURIComponent(caBucket())];
  if (state.ca.complexity) qs.push('complexity=' + encodeURIComponent(state.ca.complexity));
  // Ticket-only, and only when actually narrowing: the server treats an absent `itypes` as
  // every type, so sending an all-selected list would be a filter that filters nothing.
  if (state.ca.kind === 'ticket' && state.ca.itype) qs.push('itypes=' + encodeURIComponent(state.ca.itype));
  var myReq = ++caReqId;
  caCurves = null;
  renderFlow(); // show a loading state in the throughput panel
  // The curves feed the throughput panel (and the KPI override) whichever split panel 2
  // is on, so this fetch always runs; the epic breakdown and the team curves are their
  // own independent requests, kicked off together rather than chained — none needs
  // another's answer. Every data-changing control funnels through here, so this is the
  // ONE place the sibling fetches re-run.
  if (state.ca.by === 'epic') loadBreakdown();
  loadCaTeams(); // no-op for non-admins; feeds panel 3 + both team splits
  loadUnconverted(); // the always-on detail table tracks the same window/kind/scope
  var land = function (d) {
    if (myReq !== caReqId) return;
    if (!d || d.error) {
      // The error must reach EVERY consumer of this response, or a panel sits
      // on Loading… forever. The burn body is only touched when the combined
      // chart owns it (a team/epic split was painted by its own fetch); the
      // throughput panel reads caCurves in its none mode, so the marker lets
      // renderFlow show its own error state.
      caCurves = { error: true };
      if (!caSplit()) { var b = $('#ca-burn'); if (b) b.innerHTML = '<div class="empty">No data.</div>'; }
      renderFlow();
      return;
    }
    caCurves = d;
    // The options are discovered per window and scope, so a selection can name a type this
    // window has none of — the panel would then sit empty under a filter the user can no
    // longer see. Reconciling only ever widens, so the response in hand is NARROWER than the
    // reconciled filter claims: refetch once to make the chart match its own label.
    // Terminates because reconcile is idempotent for a given option set (see itypeFilter).
    var reconciled = itypeReconcile(state.ca.itype, d.itypeOptions || []);
    if (reconciled !== state.ca.itype) {
      state.ca.itype = reconciled;
      loadCostArtifact();
      return;
    }
    renderBurn(d);
    renderFlow();
  };
  // Rejections (network drop, a proxy's HTML error page) land in the same
  // error state as an error response — never an eternal Loading…
  get('/api/cost-artifact?' + qs.join('&') + scopeQs()).then(land, function () { land(null); });
}

// The categorical breakdown of the SAME money the burn band holds, re-bucketed by ticket
// type / epic / the children of a drilled-in item (Store.costBreakdown). Kept apart from
// caCurves because the two axes are fetched independently: changing the axis or drilling
// in re-runs only this, since neither moves the window, the kind or the throughput.
var caBreakdown: any = null;
var caBdReqId = 0;

export function loadBreakdown() {
  if (state.ca.by !== 'epic') return; // the team split has its own fetch (loadCaTeams)
  var myReq = ++caBdReqId;
  caBreakdown = null;
  renderBreakdown(); // loading state, and it clears the previous level's bars
  var qs = 'by=' + encodeURIComponent(state.ca.by) + '&days=' + encodeURIComponent(String(state.days)) +
    (state.ca.within ? '&within=' + encodeURIComponent(state.ca.within) : '');
  get('/api/cost-breakdown?' + qs + scopeQs()).then(function (d) {
    if (myReq !== caBdReqId) return;
    caBreakdown = d && !d.error ? d : { rows: [], total: 0, converted: 0, wontDo: 0 };
    renderBreakdown();
  });
  // The breadcrumb is read back from the server rather than accumulated as the user
  // drills, so the chain is right even for a level reached some other way, and there is
  // one source of truth for "where am I". Guarded by the same req id: a fast crumb from
  // a superseded drill-in must not label the level that replaced it.
  if (!state.ca.within) { state.ca.crumb = []; return; }
  get('/api/artifact-crumb?id=' + encodeURIComponent(state.ca.within)).then(function (d) {
    if (myReq !== caBdReqId) return;
    state.ca.crumb = (d && d.crumb) || [];
    renderBreakdown();
  });
}

function renderBreakdown() {
  var el = $('#ca-burn');
  if (!el) return;
  var crumb = state.ca.crumb || [];
  var here = crumb.length ? crumb[crumb.length - 1] : null;
  var sub = $('#ca-burn-sub');
  if (sub) {
    // The unconverted figure rides the subtitle here (the header line belongs
    // to the breadcrumb): the WINDOW's pale total, deliberately not the
    // drilled-in level's — it labels the table below, which never drills.
    sub.innerHTML = esc(caWinLabel()) + ' &middot; ' +
      (here ? 'inside ' + esc(here.ident || here.id) : 'by epic') + unconvLink();
    wireUnconvLink();
  }
  renderBurnNote();
  var leg = $('#ca-burn-legend');
  if (leg) {
    leg.innerHTML =
      '<span class="leg"><span class="swatch" style="background:#0f7a55"></span>converted (its Jira item closed)</span>' +
      // Neutral on purpose. `wont_do` is a decision, not a failure — the same stance the
      // ticket table's badge takes (grey, never the red failure colour). The band exists
      // because this spend can't join the green one, but that is a fact about the metric,
      // not a verdict on the work, so the parenthetical states the state and stops.
      '<span class="leg"><span class="swatch" style="background:#c1785f"></span>won’t do (closed unshipped — counted, not converted)</span>' +
      '<span class="leg"><span class="swatch" style="background:#ece7dc"></span>in flight (still open)</span>';
  }
  var head = $('#ca-burn-head');
  var d = caBreakdown;
  if (!d) {
    if (head) head.innerHTML = '';
    el.innerHTML = '<div class="empty">Loading…</div>';
    return;
  }
  if (head) {
    // Breadcrumb: every level but the last is a link back up. "All work" is the top, and
    // it is a link too — the only way back out of a drill-in.
    var parts = ['<a data-up="">All work</a>'];
    crumb.forEach(function (c, i) {
      var txt = esc(c.ident || c.id);
      parts.push(i === crumb.length - 1 ? '<b>' + txt + '</b>' : '<a data-up="' + esc(c.id) + '">' + txt + '</a>');
    });
    var pct = d.total > 0 ? Math.round((d.converted / d.total) * 100) : 0;
    // The total is the total OF THIS LEVEL — at the top that is every dollar in the window
    // that reached a Jira item (so the bars reconcile with the burn's converted band, NOT
    // with all spend), and inside a drill-in it is the subtree's.
    head.innerHTML =
      '<div class="bd-head">' +
        '<span class="bd-crumb">' + parts.join('<span class="sep">&rsaquo;</span>') + '</span>' +
        '<span class="bd-ctl">' + esc(usd(d.total)) + ' spent &middot; ' + pct + '% converted' +
          // "won’t do", matching the legend swatch and the ticket table's badge, rather than
          // "cancelled" — three words for one state read as three different things.
          (d.wontDo > 0 ? ' &middot; ' + esc(usd(d.wontDo)) + ' won’t do' : '') + '</span>' +
      '</div>';
    Array.prototype.forEach.call(head.querySelectorAll('[data-up]'), function (a) {
      a.onclick = function () {
        state.ca.within = a.getAttribute('data-up') || '';
        loadBreakdown();
      };
    });
  }
  el.innerHTML = breakdownBars((d.rows || []).map(bdRow), 'data-bd', 'data-bd-sess');
  var drill = function (id) {
    if (!id) return;
    state.ca.within = id;
    loadBreakdown();
  };
  Array.prototype.forEach.call(el.querySelectorAll('[data-bd]'), function (row) {
    row.onclick = function () { drill(row.getAttribute('data-bd')); };
    // The rows are focusable buttons (role="button"), so Enter/Space must do what a click
    // does — otherwise drill-in is mouse-only.
    row.onkeydown = function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); drill(row.getAttribute('data-bd')); }
    };
  });
  // A terminal bar's "Sessions →" — same destination and same query as the Artifacts
  // tab's button, so one artifact has ONE session population however you reach it.
  Array.prototype.forEach.call(el.querySelectorAll('[data-bd-sess]'), function (btn) {
    btn.onclick = function (e) {
      // A terminal row is not drillable, so no row handler fires — but the drillable-row
      // handler is on the ROW, and a future markup change could put this button inside
      // one. Stopping here keeps the button's meaning local either way.
      e.stopPropagation();
      openBarSessions(btn.getAttribute('data-bd-sess'), btn.getAttribute('data-art'),
        btn.getAttribute('data-subtree') === '1');
    };
  });
}

/**
 * A terminal bar's action: jump to the Sessions list filtered to that one artifact, and
 * leave a way back to the exact breakdown level the user left from.
 *
 * The destination is `filterByArtifactId`, the SAME function the Artifacts tab's
 * "Sessions →" calls, matched on the artifact id — never on its key as text, because
 * `AL-1` as a substring also matches AL-10 … AL-149.
 *
 * The window is deliberately ALL TIME (filterByArtifactId's default), not the dashboard
 * window: the bar's dollars are spend *in* the window, but a session is where the work
 * happened, and clipping the list to the window would hide the sessions that earned the
 * earlier half of the bar. The banner says so, because the two numbers otherwise look
 * like they should match.
 */
function openBarSessions(id, label, subtree) {
  if (!id) return;
  // Where the user is standing, captured NOW — the panel keeps living while they are on
  // the Sessions tab (a kind toggle resets the axis, a window change refetches), so the
  // way back has to carry its own coordinates rather than read them on arrival.
  var from = { by: state.ca.by || 'epic', within: state.ca.within || '', kind: state.ca.kind };
  var here = (state.ca.crumb || []).length ? state.ca.crumb[state.ca.crumb.length - 1] : null;
  var backLabel = here ? 'the ' + (here.ident || here.id) + ' breakdown' : 'the epic breakdown';
  // Set the grounding frame BEFORE navigating: filterByArtifactId re-renders the sessions
  // view, and renderAskBanner mounts into a slot that lives above it.
  state.asked = {
    q: 'Sessions that worked on ' + (label || id) + (subtree ? '' : ' itself') +
      ' — all time, not just the ' + caWinLabel() + ' the bar covers.',
    answer: '',
    about: 'Sessions are matched the same way the money is: work attributed to this item ' +
      'in a session, not a passing mention of it. A session appears here whole, so its ' +
      'cost is the whole session, not the slice charged to this item. Open one and its ' +
      'transcript starts focused on the turns this item was charged for.',
    section: 'sessions',
    // Restores the LEVEL, not just the tab — the same bars, under the same axis and the
    // same ticket kind, even if something else moved the panel meanwhile.
    back: { label: backLabel, run: function () { returnToBreakdown(from); } },
  };
  // No kind: these bars are Jira items, and filterByArtifactId defaults an id-only
  // drill-in to the ticket dimension — which is also right for an epic bar, whose money
  // lives in its subtasks' blocks (see blkUnder).
  filterByArtifactId(id, label, subtree);
  renderAskBanner(true);
}

/**
 * Back from a leaf's session list to the breakdown level it was opened from.
 *
 * `from` = the {kind, by, within} captured when the button was clicked. Restoring all
 * three is what makes this a return rather than a re-entry: `kind` because the ticket
 * toggle scopes the whole section (and switching it clears the axis), `by` because
 * landing on the time axis would show a chart with no bars to have come from, and
 * `within` because the level IS where the user was.
 */
function returnToBreakdown(from) {
  // A kind change re-words every panel in the section (CA_KINDS drives the titles, the
  // legend and the notes), so it needs the whole detail rebuilt; the axis and the level
  // only move the bars.
  var kindMoved = state.ca.kind !== from.kind;
  var barsMoved = state.ca.by !== from.by || (state.ca.within || '') !== from.within;
  state.ca.kind = from.kind;
  state.ca.userPicked = true; // an explicit return, so the smart default must not undo it
  state.ca.by = from.by;
  state.ca.within = from.within;
  setView('dashboard');
  // Whether the panels are still MOUNTED is the other question, and #ca-burn answers it
  // directly — the tab switch only hid them, so in the normal case they are, and a full
  // re-render would refetch the treemap and both curves to redraw what is already on
  // screen.
  if (kindMoved || state.metric !== 'cost_artifact' || !$('#ca-burn')) openMetric('cost_artifact', true);
  // renderBurnCtl too, not just the section controls: the split select and the
  // Bucket seg live in the burn panel's header now, and this path just moved
  // the split back to the epic axis — without it the select keeps whatever the
  // user changed it to after this back-closure was captured.
  else if (barsMoved) { renderCaControls(); renderBurnCtl(); loadBreakdown(); }
  var panel = $('#ca-burn-head') || $('#ca-burn');
  if (panel) panel.scrollIntoView({ block: 'center' });
}

// The notes under the burn panel. Shared by BOTH axes (renderBurn's time path and
// renderBreakdown's categorical one) because they describe the population, which is the
// same either way — when this lived inline in renderBurn, switching to a categorical axis
// left the previous axis's wording in place.
function renderBurnNote() {
  var K = caK();
  var el = $('#ca-burn-note');
  if (!el) return;
  var parts: string[] = [];
  // The panel's denominator is spend that reached a `kind` artifact, not all AI spend.
  // Naming the excluded figure is not a footnote — it is the honest half of the metric:
  // without it a reader cannot tell a 74% conversion rate over most of the money from the
  // same rate over a sliver of it. It is also the actual finding (live: $1,196.80 excluded
  // on the ticket tab, two-thirds of which shipped a PR — real work, just not ticketed).
  var d = caCurves;
  if (caSplit() === 'team') {
    // The org-total figures below would read wrong against per-team bars: teams
    // can overlap (member of one, repo of another) and teamless work is under
    // no bar, so the bars need not sum to the org denominator.
    parts.push('Each bar counts spend that reached a ' + K.unit + ', scoped to the team the way the team ' +
      'filter scopes it. A session can count under two teams, and teamless users’ work appears under no bar — ' +
      'so the bars need not sum to the combined view.');
  } else if (d && d.burn) {
    var all = 0, attributed = 0;
    d.burn.forEach(function (r) { all += r.spend || 0; attributed += r.attributedSpend || 0; });
    var off = all - attributed;
    parts.push('Counts spend that reached a ' + K.unit + ' — the denominator is ' + usd(attributed) +
      ', not total AI spend.' +
      (off > 0.005
        ? ' A further ' + usd(off) + ' in this window touched no ' + K.unit +
          ' at all; it is not unconverted work here, it is work of another kind (see the ' +
          (state.ca.kind === 'ticket' ? 'Feature and PR views' : 'other views') + ').'
        : ''));
  }
  // The complexity filter narrows only the converted line — spend can't be filtered by an
  // attribute of an artifact it never reached, so the denominator stays whole. Flag that so
  // the "unconverted" band isn't read as "never shipped" when it also holds
  // other-complexity spend.
  if (state.ca.complexity) {
    parts.push('With a complexity filter on, only the converted line is filtered: it counts spend linked to a ' +
      K.verb + ' ' + K.unit + ' of the selected complexity, so spend on ' + K.units +
      ' of other complexity falls into the unconverted band.');
  }
  // Tickets: converted is judged per BLOCK against the item that block was attributed to,
  // with no rollup. Spend on a subtask that is still open stays unconverted even once its
  // epic closes — otherwise closing one parent would retro-green months of in-flight work.
  // Worth saying, because the hierarchy makes the other reading plausible.
  if (state.ca.kind === 'ticket') {
    parts.push('Converted means the Jira item that spend was attributed to is itself closed — ' +
      'an epic closing does not convert spend charged to its still-open children.');
  }
  el.innerHTML = esc(parts.join(' '));
}

function renderBurn(d) {
  // Never render the error sentinel: no KPI to mirror, no chart to draw.
  if (!d || d.error) return;
  // Mirror the complexity-filtered unit cost into the headline tile. The tile is
  // sourced from /api/kpis (unfiltered), so when a filter is active we hand it the
  // figure this endpoint already computed for the same kind/window/complexity/scope
  // (paintKpis' useCaFilter matches on all four, so a scope change can't paint stale).
  setCaKpiOverride(
    state.ca.complexity
      ? { kind: state.ca.kind, days: state.days, complexity: state.ca.complexity, scope: scopeQs(), kpi: d.kpi }
      : null,
  );
  paintKpis();
  // On a categorical axis the panel body belongs to renderBreakdown — its own fetch owns
  // #ca-burn, the header line and the legend. Everything below this point (the two notes
  // and the KPI mirror) is axis-independent, so it still runs.
  if (!caSplit()) {
    // The band's denominator is ATTRIBUTED spend, not all spend — the same population the
    // categorical axis bars partition, so switching axes under one control can't change
    // the total. `spend` is still served and becomes the untracked note below.
    var burnPts = (d.burn || []).map(function (r) { return { bucket: r.bucket, total: r.attributedSpend, filled: r.shippedSpend }; });
    var el = $('#ca-burn');
    if (el) el.innerHTML = stackChart(d.buckets || [], burnPts, 'usd');
    var head0 = $('#ca-burn-head');
    // The time axis gets the same header line as the categorical one: without it, the two
    // axes would disagree about whether the panel states its own total.
    if (head0) {
      var tot = 0, conv = 0;
      (d.burn || []).forEach(function (r) { tot += r.attributedSpend || 0; conv += r.shippedSpend || 0; });
      // The pale band's total rides the SAME helper the split subtitles use
      // (unconvLink) — one rendering of one figure, wherever it appears.
      head0.innerHTML = tot > 0
        ? '<div class="bd-head"><span class="bd-crumb"></span><span class="bd-ctl">' +
            esc(usd(tot)) + ' spent &middot; ' + Math.round((conv / tot) * 100) + '% converted' + unconvLink() +
            '</span></div>'
        : '';
      wireUnconvLink();
    }
    // Restore the time-axis wording: renderBreakdown rewrote both when the axis was
    // categorical, and this is the path back (the panel markup is only rebuilt on a
    // kind change, not on an axis change).
    var sub0 = $('#ca-burn-sub');
    if (sub0) sub0.innerHTML = esc(caWinLabel()) + ' &middot; dated at session start time';
    var leg0 = $('#ca-burn-legend');
    if (leg0) {
      leg0.innerHTML =
        '<span class="leg"><span class="swatch" style="background:#0f7a55"></span>converted (linked to a ' +
          esc(caK().verb) + ' ' + esc(caK().unit) + ')</span>' +
        '<span class="leg"><span class="swatch" style="background:#ece7dc"></span>unconverted &mdash; in-flight or never ' +
          esc(caK().verb) + '</span>';
    }
  } else if (caSplit() === 'team') {
    // The split modes' subtitles carry the unconverted figure, which is
    // computed from THIS response — if it landed after the split renderer
    // painted, refresh that subtitle now (cheap: repaints from its own cache).
    renderTeamBurn();
  } else {
    renderBreakdown();
  }
  renderBurnNote();
  var note = 'Unit cost includes every session that contributed to these ' + caNoun() +
    ', even spend from before the window — so it will not equal spend within the window.';
  if (state.ca.kind === 'pr') {
    note += ' Every merged PR you contributed to counts — authored or reviewed via gh in a' +
      ' captured session; PRs merged outside a captured session aren\'t counted (yet).';
  }
  if (state.ca.kind === 'ticket') {
    note += ' Every level of the tree counts as one item, so an epic and its stories are each' +
      ' a unit and spend shared between them is charged to both — the treemap above unions it instead.';
  }
  var n = $('#ca-note');
  if (n) n.innerHTML = esc(note);
}

function renderFlow() {
  var el = $('#ca-flow-chart');
  if (!el) return;
  var pr = state.ca.kind === 'pr';
  var K = caK();
  var byTeam = caFlowSplit() === 'team';
  var opts: ItypeOption[] = (caCurves && caCurves.itypeOptions) || [];
  var titleEl = $('#ca-flow-title');
  if (titleEl) {
    // The filter clause goes in the SUBTITLE, not only in the checkboxes: this chart's
    // height moves with the filter, so what it counts has to be readable from the panel.
    var fsub = itypeSubLabel(state.ca.itype, opts);
    titleEl.innerHTML = esc(K.flow) +
      ' <span class="metric-sub">' + esc(caWinLabel()) + (byTeam ? ' &middot; by team' : '') +
      ' &middot; dated at ' + (pr ? 'merge' : 'completion') + ' time' +
      (fsub ? ' &middot; ' + esc(fsub) : '') + '</span>';
  }
  renderItypeCtl(opts);
  var leg = $('#ca-flow-legend');
  // An empty chart under a filter points at the filter first — otherwise the reader widens
  // the window, gets nothing again, and concludes there is no data. Shared by BOTH modes:
  // the team lines are itype-filtered too (loadCaTeams sends it), so an all-zero team view
  // is the same state and deserves the same pointer.
  var emptyMsg = '<div class="empty">' + esc(state.ca.itype
    ? 'No ' + K.units + ' of the selected type ' + K.verb + ' in this window — widen the Type selection or the window above.'
    : 'No ' + K.units + ' ' + K.verb + ' in this window — ' + K.hint + ', or widen the window above.') + '</div>';
  if (byTeam) {
    // One line per team, from the same response panel 3 and the burn's team
    // split read. The Type filter narrows these lines too (loadCaTeams sends
    // it), so both modes count the same population.
    var td = caTeams;
    if (!td || td.error) {
      el.innerHTML = '<div class="empty">' + (td ? 'No data.' : 'Loading…') + '</div>';
      if (leg) leg.innerHTML = '';
      return;
    }
    var teams = td.teams || [];
    var anyFlow = teams.some(function (t) { return t.throughput.some(function (c) { return c > 0; }); });
    if (!anyFlow) { el.innerHTML = emptyMsg; if (leg) leg.innerHTML = ''; return; }
    var lines = teams.map(function (t, i) {
      return {
        label: t.name,
        color: TEAM_COLORS[i % TEAM_COLORS.length],
        points: td.buckets.map(function (b, j) { return { bucket: b, y: t.throughput[j] }; }),
      };
    });
    el.innerHTML = valueLineChart(td.buckets, lines, 'count', K.flow);
    if (leg) leg.innerHTML = teamLegend(teams);
    return;
  }
  if (leg) leg.innerHTML = '';
  var d = caCurves;
  if (!d) { el.innerHTML = '<div class="empty">Loading…</div>'; return; }
  // An error is an ERROR here too: falling through would render the tailored
  // empty message — a business claim ("nothing closed") fabricated from a 500.
  if (d.error) { el.innerHTML = '<div class="empty">No data.</div>'; return; }
  var pts = (d.throughput || []).map(function (r) { return { bucket: r.bucket, y: r.count }; });
  if (pts.some(function (p) { return p.y > 0; })) {
    el.innerHTML = valueLineChart(d.buckets || [], [{ label: K.flow, color: '#0f7a55', points: pts }], 'count', K.flow);
    return;
  }
  el.innerHTML = emptyMsg;
}

// The Type filter, in the throughput panel's own header. Rendered from the response rather
// than alongside the other controls because the options are DISCOVERED — until the fetch
// lands there is nothing to offer, and a control that appears empty then fills reads as
// broken. Hidden for a single option too: a filter that can only be all-on is furniture.
function renderItypeCtl(opts: ItypeOption[]) {
  var el = $('#ca-itype-ctl');
  if (!el) return;
  if (state.ca.kind !== 'ticket' || opts.length < 2) { el.innerHTML = ''; return; }
  var itSel = opts.filter(function (o) { return itypeChecked(state.ca.itype, o.key); }).map(function (o) { return o.key; });
  el.innerHTML = msHtml('ca-it', 'Type', itSel.length === opts.length ? 0 : itSel.length);
  // Issue types as the shared multiselect — the itypeFilter semantics survive:
  // '' ⇒ all (badge dark), all-checked collapses to '', last box refuses (minOne).
  msWire({
    id: 'ca-it',
    options: opts.map(function (o) { return { value: o.key, label: o.label, count: o.count }; }),
    selected: itSel,
    minOne: true,
    badge: function (vals) { return vals.length === opts.length ? 0 : vals.length; },
    onChange: function (vals) {
      state.ca.itype = vals.length === opts.length ? '' : vals.join(',');
      // Only the throughput panel is filtered, so only its fetch re-runs — the treemap and
      // the KPI are deliberately untouched. loadCostArtifact repaints the burn from the same
      // response, which is unfiltered by design and so redraws identically.
      loadCostArtifact();
    },
  });
}
